/**
 * PreferencesProvider — makes the request-resolved {@link RequestPreferences} (locale +
 * timezone) available to every component, so all localized dates and numbers format from
 * ONE shared source on both the server and the client (no hydration mismatch).
 *
 * How it stays hydration-safe:
 *  - The value is seeded from the dehydrated `preferencesQueryOptions` cache (the root
 *    loader server-awaited it), so the provider's first render on the client reads exactly
 *    what the server rendered → the initial DOM matches.
 *  - After mount, a one-time reconciler adopts the browser's REAL locale + timezone and
 *    persists them to a cookie, so the NEXT SSR is exact. On repeat visits the cookie
 *    already matches the browser, so there's no state change and no re-render — the server
 *    renders the correct local time directly. Only a first visit (or a wrong CF geo hint)
 *    ever does the one text-node update, which is a normal client render (no remount, no
 *    animation restart) and lands under the entrance fade.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_PREFERENCES,
  LOCALE_COOKIE,
  PREFERENCES_QUERY_KEY,
  TZ_COOKIE,
  detectBrowserPreferences,
  formatDateTime,
  formatNumber,
  type DateVariant,
  type RequestPreferences,
} from './preferences';

const PreferencesContext = createContext<RequestPreferences>(DEFAULT_PREFERENCES);

/** Persist a preference cookie (1 year, path=/, lax) so the next SSR reads the exact value. */
function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; samesite=lax`;
}

export function PreferencesProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const queryClient = useQueryClient();
  // Seed from the dehydrated query cache — identical on server and first client render.
  const [prefs, setPrefs] = useState<RequestPreferences>(
    () => queryClient.getQueryData<RequestPreferences>(PREFERENCES_QUERY_KEY) ?? DEFAULT_PREFERENCES,
  );

  useEffect(() => {
    const browser = detectBrowserPreferences();
    // Always pin the exact browser values into cookies so the next request's SSR renders
    // precisely (upgrades a CF-geo / UTC first-visit guess to an explicit value).
    writeCookie(TZ_COOKIE, browser.timeZone);
    writeCookie(LOCALE_COOKIE, browser.locale);
    // Update the live value only when it actually differs (first visit / wrong hint) —
    // on the common repeat visit the seeded value already equals the browser's, so this
    // returns the same object and triggers no re-render.
    setPrefs((current) =>
      current.timeZone === browser.timeZone && current.locale === browser.locale ? current : browser,
    );
  }, []);

  return <PreferencesContext.Provider value={prefs}>{children}</PreferencesContext.Provider>;
}

/** The visitor's resolved locale + timezone. */
export function usePreferences(): RequestPreferences {
  return useContext(PreferencesContext);
}

/** A bound date/time formatter (hydration-safe) using the current preferences. */
export function useFormatDateTime(): (iso: string, variant?: DateVariant) => string {
  const prefs = usePreferences();
  return useMemo(() => (iso: string, variant: DateVariant = 'datetime') => formatDateTime(iso, prefs, variant), [prefs]);
}

/** A bound number formatter using the current preferences' locale. */
export function useFormatNumber(): (value: number, opts?: Intl.NumberFormatOptions) => string {
  const prefs = usePreferences();
  return useMemo(() => (value: number, opts?: Intl.NumberFormatOptions) => formatNumber(value, prefs, opts), [prefs]);
}
